import { DOWN_ARROW, UP_ARROW } from '@angular/cdk/keycodes';
import { NgClass } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  signal,
  WritableSignal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { IconComponent } from '@shared/ui-shared';
import { InputPatternDirective } from '../../directives/input-pattern/input-pattern.directive';

const baseClx: string[] = [
  'counter-root',
  'flex items-center gap-2',
  'py-3 px-4 w-full',
  'border rounded-xl outline-none',
  'group-[.g-control-invalid]:border-[--counter-error-border-color]',
  'focus-within:outline focus-within:outline-2 focus-within:outline-offset-2',
];
const filledClx: string[] = [
  'text-[--counter-text-color]',
  'border-[--counter-border-color]',
  'bg-[--counter-bg-color]',
];
const disabledClx: string[] = [
  'text-[--counter-disabled-text-color]',
  'border-[--counter-disabled-border-color]',
  'bg-[--counter-disabled-bg-color]',
];

@Component({
  selector: 'csl-counter',
  standalone: true,
  imports: [NgClass, InputPatternDirective, IconComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: CounterComponent,
    },
  ],
  templateUrl: './counter.component.html',
  styleUrl: './counter.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(keydown)': 'handleKeydown($event)',
    '(focusout)': 'blur();',
  },
})
export class CounterComponent implements ControlValueAccessor {
  private cdr = inject(ChangeDetectorRef);
  public touched = false;
  public readonly Number = Number;
  public readonly value: WritableSignal<number | null> = signal(null);

  private readonly baseClasses = baseClx.join(' ');
  private readonly filledClasses = filledClx.join(' ');
  private readonly disabledClasses = disabledClx.join(' ');

  public get classes(): string {
    const focusOutlineColor = this.inverted ? 'focus-within:outline-white' : 'focus-within:outline-focus';
    if (this.disabled) {
      return `${this.baseClasses} ${this.disabledClasses}`;
    }
    return `${this.baseClasses} ${this.filledClasses} ${focusOutlineColor}`;
  }

  @Input() public bottomLimit = 1;
  @Input() public id = '';
  @Input({ required: true }) public topLimit!: number;
  @Input() public withInput = false;
  @Input() public size: 'sm' | 'md' = 'md';
  @Input() public ariaInvalid?: string | null;
  @Input() public ariaDescribedby?: string | null;
  @Input() public ariaRequired?: boolean;
  @Input() public ariaLabel?: string;
  @Input() public ariaLabelledby?: string;
  @Input() public inverted = false;

  public readonly numberPattern = /^\d{1,2}$/;

  private _disabled = false;

  @Input({ transform: booleanAttribute })
  public get disabled(): boolean {
    return this._disabled;
  }

  public set disabled(value: boolean) {
    this._disabled = value;
  }

  public onTouched = () => {
    /**
     *
     */
  };

  public onChange: (value: number | null) => void = () => {
    /**
     *
     */
  };

  public writeValue(value: number) {
    this.value.set(value);
  }

  public registerOnChange(onChange: (value: number | null) => void) {
    this.onChange = onChange;
  }

  public registerOnTouched(onTouched: () => void) {
    this.onTouched = onTouched;
  }

  public markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  public blur() {
    this.markAsTouched();
  }

  public setDisabledState(disabled: boolean) {
    this.disabled = disabled;
    this.cdr.detectChanges();
  }

  public handleKeydown(event: KeyboardEvent): void {
    if (!this.disabled) {
      const keyCode = event.keyCode;
      if (keyCode === DOWN_ARROW) {
        this.decrement();
        event.preventDefault(); // prevents the page from scrolling down when pressing
      } else if (keyCode === UP_ARROW) {
        this.increment();
        event.preventDefault(); // prevents the page from scrolling down when pressing
      }
    }
  }

  public increment(): void {
    const newValue = Number(this.value()) + 1;
    if (!this.disabled && this.isInLimitRange(newValue)) {
      this.value.set(newValue);
      this.onChange(this.value());
    }
  }

  public inputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const newValue = Number(input.value);
    this.value.set(newValue);
    this.cdr.detectChanges();

    if (this.disabled) return;

    if (this.isInLimitRange(newValue)) {
      this.value.set(newValue);
    }
    if (newValue < this.bottomLimit) {
      this.value.set(this.bottomLimit);
    }
    if (newValue > this.topLimit) {
      this.value.set(this.topLimit);
    }
    this.onChange(this.value());
  }

  public decrement(): void {
    const newValue = Number(this.value()) - 1;
    if (!this.disabled && this.isInLimitRange(newValue)) {
      this.value.set(newValue);
      this.onChange(this.value());
    }
  }

  private isInLimitRange(value: number): boolean {
    return !(value < this.bottomLimit || value > this.topLimit);
  }
}
